import { useState, type FC } from 'react'
import { type SchedulingPageStatus } from '../useSchedulingPageStatus'
import { CalendarStep } from './CalendarStep'
import { EventTypesStep } from './EventTypesStep'
import { MeetingPlatformStep } from './MeetingPlatformStep'
import { ONBOARDING_STEP_ORDER, OnboardingStep, stepForStatus } from './onboardingSteps'
import { OnboardingStepFrame } from './OnboardingStepFrame'
import { OnboardingStepper } from './OnboardingStepper'
import { SchedulingUnavailableState } from './SchedulingUnavailableState'
import { SkipMeetingPlatformModal } from './SkipMeetingPlatformModal'

export interface SchedulingOnboardingFlowProps {
  status: SchedulingPageStatus
  onSkipMeetingPlatform: () => void

  mode?: 'onboarding' | 'unblock'
}

const previousStepOf = (step: OnboardingStep): OnboardingStep | undefined => ONBOARDING_STEP_ORDER[ONBOARDING_STEP_ORDER.indexOf(step) - 1]

interface BackOverride {
  step: OnboardingStep
  baseStep: OnboardingStep
}

export interface OnboardingFlowStepProps {
  onCompleted?: () => void
}

const ONBOARDING_STEP_COMPONENT_MAP: Record<OnboardingStep, FC<OnboardingFlowStepProps>> = {
  [OnboardingStep.CALENDAR]: CalendarStep,
  [OnboardingStep.MEETING_PLATFORM]: MeetingPlatformStep,
  [OnboardingStep.EVENT_TYPES]: EventTypesStep,
}

const OnboardingFlowStep: FC<OnboardingFlowStepProps & { step: OnboardingStep }> = ({ step, onCompleted }) => {
  const StepComponent = ONBOARDING_STEP_COMPONENT_MAP[step]
  return <StepComponent onCompleted={onCompleted} />
}

export const SchedulingOnboardingFlow: FC<SchedulingOnboardingFlowProps> = ({ status, onSkipMeetingPlatform, mode = 'onboarding' }) => {
  const [backOverride, setBackOverride] = useState<BackOverride | null>(null)
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false)

  if (status.kind === 'account-blocked') {
    return <SchedulingUnavailableState isAdmin={status.isAdmin} />
  }

  const derivedStep = stepForStatus(status)
  if (derivedStep == null) {
    return null
  }

  const activeStep = backOverride?.baseStep === derivedStep ? backOverride.step : derivedStep
  const previousStep = previousStepOf(activeStep)

  const isOnboarding = mode === 'onboarding'

  return (
    <div className='d-flex flex-column align-items-center gap-4 w-100'>
      {isOnboarding ? <OnboardingStepper activeStep={activeStep} /> : null}
      <OnboardingStepFrame
        onBack={isOnboarding && previousStep ? () => setBackOverride({ step: previousStep, baseStep: derivedStep }) : undefined}
        onSkip={isOnboarding && activeStep === OnboardingStep.MEETING_PLATFORM ? () => setIsSkipModalOpen(true) : undefined}
      >
        <OnboardingFlowStep step={activeStep} onCompleted={() => setBackOverride(null)} />
      </OnboardingStepFrame>
      {isSkipModalOpen ? (
        <SkipMeetingPlatformModal
          onConfirm={() => {
            setIsSkipModalOpen(false)
            setBackOverride(null)
            onSkipMeetingPlatform()
          }}
          onCancel={() => setIsSkipModalOpen(false)}
        />
      ) : null}
    </div>
  )
}
